import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { displayTripStatus } from '../../utils/actingTripStatus';
import { useQuery } from 'react-query';
import {
  ArrowLeft,
  MapPin,
  Clock,
  User,
  Star,
  AlertCircle,
  Navigation,
  Calendar,
  Eye,
  X,
  CheckCircle,
  XCircle,
  Ban,
  Play,
  Edit,
  Users
} from 'lucide-react';
import { api } from '../../utils/api';
// import { useAuth } from '../../contexts/AuthContext';
import TripMap from '../../components/TripMap';
import RideMatchMap from '../../components/RideMatchMap';
import RideMatchModal from '../../components/Trips/RideMatchModal';
import ActingTripMedia from '../../components/Trips/ActingTripMedia';
import { cancelReasons } from '../../components/driverCancelReasons';

const TripDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  // const navigate = useNavigate();
  // const { hasPermission } = useAuth();
  const [showMap, setShowMap] = useState(true);
  const [showEditStatusModal, setShowEditStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [statusChangeReason, setStatusChangeReason] = useState('');
  const [statusChangeReasonOther, setStatusChangeReasonOther] = useState('');
  const [showTripJsonModal, setShowTripJsonModal] = useState(false);
  const [showFareJsonModal, setShowFareJsonModal] = useState(false);
  const [timelineMode] = useState('cumulative'); // 'step' or 'cumulative'
  const [driverLocation, setDriverLocation] = useState(null);
  const [showRideMatchMap, setShowRideMatchMap] = useState(false);
  const [showRideMatchModal, setShowRideMatchModal] = useState(false);
  const [rideMatchDriverLocations, setRideMatchDriverLocations] = useState([]);
  const [availableSectionOpen, setAvailableSectionOpen] = useState(false);
  const [rankedSectionOpen, setRankedSectionOpen] = useState(false);
  const [expandedAvailableDrivers, setExpandedAvailableDrivers] = useState(new Set());
  const [expandedRankedDrivers, setExpandedRankedDrivers] = useState(new Set());

  // Prevent background scrolling when any modal is open
  useEffect(() => {
    const anyModalOpen = showTripJsonModal || showFareJsonModal || showEditStatusModal || showConfirmModal;
    if (anyModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [showTripJsonModal, showFareJsonModal, showEditStatusModal, showConfirmModal]);

  // Primary trip data fetch
  const { data, isLoading, error } = useQuery(
    ['trip', id],
    () => api.get(`/trips/${id}`),
    {
      enabled: !!id
    }
  );

  // Extract trip data
  const tripData = data?.data?.data || data?.data;
  const trip = tripData;

  // Fetch driver location if trip is active (ACCEPTED or PICKEDUP)
  const shouldFetchDriverLocation = !!(trip && (trip.status === 'ACCEPTED' || trip.status === 'PICKEDUP') && trip.driverId?._id);
  
  const { data: driverData, refetch: refetchDriverLocation } = useQuery(
    ['driver-location', trip?._id, trip?.driverId?._id],
    async () => {
      if (!trip?._id) return null;
      try {
        // Use the new dedicated driver-location endpoint
        const response = await api.get(`/trips/${trip._id}/driver-location`);
        return response?.data?.data || null;
      } catch (error) {
        console.warn('Failed to fetch driver location:', error);
        return null;
      }
    },
    {
      enabled: shouldFetchDriverLocation,
      // Follow the driver while the trip is live (accepted or on the way). It was
      // manual-refresh only, so the marker stayed wherever the page was opened and
      // looked like a location that "does not update". Pauses in a hidden tab.
      refetchInterval: shouldFetchDriverLocation ? 10 * 1000 : false,
      refetchIntervalInBackground: false,
      retry: 1,
      staleTime: 5 * 1000,
      cacheTime: 5 * 60 * 1000 // Keep in cache for 5 minutes
    }
  );

  // The route driven SO FAR, for a trip that is waiting for pickup or under way. The
  // finished route is saved on the trip when it ends, so until then the map had only
  // markers and no line even though the driver's GPS points were arriving. Asked of
  // the rides backend (which holds the trail) and redrawn every 10 seconds.
  const { data: liveRouteData } = useQuery(
    ['trip-live-route', trip?._id],
    async () => {
      try {
        const response = await api.get(`/trips/${trip._id}/live-route`);
        return response?.data?.data || null;
      } catch (error) {
        console.warn('Failed to fetch the live route:', error);
        return null;
      }
    },
    {
      enabled: !!(shouldFetchDriverLocation && trip?._id && !trip?.encodedPolyline),
      refetchInterval: 10 * 1000,
      refetchIntervalInBackground: false,
      retry: 1,
      staleTime: 5 * 1000,
    },
  );
  const liveRoutePolyline = liveRouteData?.encodedPolyline || '';

  // The trip as the map should see it: its own saved route if it has one, else the
  // route so far. Memoised on the polyline string so the map redraws only when the
  // line actually changes, not on every unrelated re-render.
  const mapTrip = React.useMemo(() => {
    if (!trip || trip.encodedPolyline || !liveRoutePolyline) return trip;
    return { ...trip, encodedPolyline: liveRoutePolyline };
  }, [trip, liveRoutePolyline]);

  // Update driver location when driver data changes
  useEffect(() => {
    if (driverData && driverData.coordinates) {
      // Driver location data from the dedicated endpoint
      setDriverLocation({
        coordinates: driverData.coordinates, // [lng, lat]
        name: driverData.name || trip?.driverId?.name || 'Driver',
        phone: driverData.phone || trip?.driverId?.phone || '',
        // The real time of the position. Falling back to "now" made an old position
        // read as fresh; with none known the map shows no time rather than a false one.
        lastUpdated: driverData.lastUpdated || null
      });
    } else if (shouldFetchDriverLocation && trip?.driverId?.location?.coordinates) {
      // Fallback to trip's driver location if available. Only while the trip is live: after
      // it ends this is where the driver is NOW, which has nothing to do with the trip and
      // was drawn as "Driver (Live)" on finished trips.
      setDriverLocation({
        coordinates: trip.driverId.location.coordinates,
        name: trip.driverId.name,
        phone: trip.driverId.phone,
        lastUpdated: trip.driverId.location?.updatedAt || trip.driverId.liveStats?.lastLocationUpdatedOn || null
      });
    }
  }, [driverData, trip?.driverId]);

  // If we still don't have a driver location, try the trip tracking endpoint as a fallback
  useEffect(() => {
    let cancelled = false;
    const tryTracking = async () => {
      if (driverLocation) return; // already have a location
      if (!trip || !trip._id || !trip.driverId?._id) return;
      // Only attempt for active trips where live location is expected
      if (!['ACCEPTED', 'PICKEDUP', 'STARTED', 'IN_PROGRESS'].includes(trip.status)) return;

      try {
        const resp = await api.get(`/trips/${trip._id}/tracking`);
        const tracking = resp?.data?.data;
        const drv = tracking?.driver;
        const coords = drv?.location?.coordinates;
        if (!cancelled && coords && coords.length === 2) {
          setDriverLocation({
            coordinates: coords,
            name: drv.name || trip?.driverId?.name || 'Driver',
            phone: drv.phone || trip?.driverId?.phone || 'N/A'
          });
        }
      } catch (e) {
        // ignore tracking failures
      }
    };

    tryTracking();
    return () => { cancelled = true; };
  }, [trip, driverLocation]);

  // Handle manual refresh of driver location
  const handleRefreshDriverLocation = () => {
    refetchDriverLocation();
  };

  // Fetch ride match driver locations when map is shown
  useEffect(() => {
    if (showRideMatchMap && trip?._id) {
      const fetchRideMatchLocations = async () => {
        try {
          const response = await api.get(`/trips/${trip._id}/ride-match-locations`);
          if (response.data.success) {
            setRideMatchDriverLocations(response.data.data.driverLocations || []);
          }
        } catch (error) {
          console.error('Failed to fetch ride match locations:', error);
        }
      };
      fetchRideMatchLocations();
    }
  }, [showRideMatchMap, trip?._id]);

  // Fetch driver names for ride match log
  const [driverNames, setDriverNames] = React.useState({});
  
  useEffect(() => {
    if (!trip?.rideMatchLog) return;
    
    const fetchDriverNames = async () => {
      try {
        // Get ALL unique driver IDs from ALL events in rideMatchLog
        const driverIds = new Set();
        
        trip.rideMatchLog.forEach(log => {
          // From any event that has driver_id
          if (log.driver_id) {
            driverIds.add(log.driver_id);
          }
          // From available_drivers event
          if (log.event === 'available_drivers' && log.drivers) {
            log.drivers.forEach(d => driverIds.add(d.driver_id));
          }
          // From ranked_drivers event
          if (log.event === 'ranked_drivers' && log.top_drivers) {
            log.top_drivers.forEach(d => driverIds.add(d.driver_id));
          }
        });
        
        console.log('All driver IDs found:', Array.from(driverIds));
        
        // Fetch each driver's name
        const names = {};
        for (const driverId of Array.from(driverIds)) {
          try {
            const response = await api.get(`/drivers/${driverId}`);
            console.log(`Fetched driver ${driverId}:`, response.data);
            if (response.data.success) {
              const driver = response.data.data?.driver || response.data.data;
              names[driverId] = {
                name: driver.name || 'Unknown Driver',
                phone: driver.phone || 'N/A'
              };
            }
          } catch (error) {
            console.error(`Failed to fetch driver ${driverId}:`, error);
            names[driverId] = { name: 'Unknown Driver', phone: 'N/A' };
          }
        }
        
        console.log('Fetched driver names:', names);
        setDriverNames(names);
      } catch (error) {
        console.error('Failed to fetch driver names:', error);
      }
    };
    
    fetchDriverNames();
  }, [trip?.rideMatchLog]);

  // Timeline helpers: normalize timestamps and format duration between states
  const normalizeTs = (ts) => {
    if (ts == null) return null;
    const n = Number(ts);
    if (Number.isNaN(n)) return null;
    return n < 1e12 ? n * 1000 : n;
  };

  const formatDurationShort = (ms) => {
    if (ms == null) return 'N/A';
    const sec = Math.round(ms / 1000);
    if (sec < 60) return `${sec}s`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ${sec % 60}s`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ${min % 60}m`;
    const days = Math.floor(hr / 24);
    return `${days}d ${hr % 24}h`;
  };

  const tripTimeline = (trip?.tripTimeline || []).map((entry, idx, arr) => {
    const ts = normalizeTs(entry.timestamp);
    const prevTs = idx > 0 ? normalizeTs(arr[idx - 1].timestamp) : null;
    const diff = prevTs != null && ts != null ? ts - prevTs : null;
    return { ...entry, timestampMs: ts, diffMs: diff };
  });

  const getTimelineBadgeClass = (state) => {
    if (!state) return 'bg-indigo-500';
    const s = String(state).toLowerCase();
    if (s.includes('accept')) return 'bg-green-500';
    if (s.includes('picked') || s.includes('pick')) return 'bg-blue-500';
    if (s.includes('cancel')) return 'bg-red-500';
    if (s.includes('diverg')) return 'bg-yellow-500';
    if (s.includes('fail')) return 'bg-gray-500';
    return 'bg-indigo-500';
  };

  // Fetch publicRidesPayments data for this trip
  const publicRidesPayments = React.useMemo(() => {
    // The payment data is now included in the trip response as paymentInfo
    return trip?.paymentInfo || null;
  }, [trip]);

  // Build driver map from trip response data
  const driverMap = React.useMemo(() => {
    const map = {};
    
    if (!trip?.rideMatchLog) return map;
    
    // First, collect ALL driver IDs from all events
    const allDriverIds = new Set();
    trip.rideMatchLog.forEach(log => {
      if (log.driver_id) allDriverIds.add(log.driver_id);
      if (log.event === 'available_drivers' && log.drivers) {
        log.drivers.forEach(d => allDriverIds.add(d.driver_id));
      }
      if (log.event === 'ranked_drivers' && log.top_drivers) {
        log.top_drivers.forEach(d => allDriverIds.add(d.driver_id));
      }
    });
    
    // Initialize map with all driver IDs
    allDriverIds.forEach(driverId => {
      map[driverId] = {
        name: driverNames[driverId]?.name || 'Loading...',
        phone: driverNames[driverId]?.phone || 'N/A'
      };
    });
    
    // Extract driver details from rideMatchLog events
    trip.rideMatchLog.forEach(log => {
      // From available_drivers event
      if (log.event === 'available_drivers' && log.drivers) {
        log.drivers.forEach(driver => {
          if (driver.driver_id && map[driver.driver_id]) {
            map[driver.driver_id] = {
              ...map[driver.driver_id],
              location: driver.location,
              totalTripsAccepted: driver.totalTripsAccepted,
              totalTripsRejected: driver.totalTripsRejected,
              isAvailable: driver.isAvailable,
              lastTripTime: driver.lastTripTime
            };
          }
        });
      }
      
      // From ranked_drivers event - merge with existing data
      if (log.event === 'ranked_drivers' && log.top_drivers) {
        log.top_drivers.forEach(driver => {
          if (driver.driver_id && map[driver.driver_id]) {
            map[driver.driver_id].rank = driver.rank;
            map[driver.driver_id].score = driver.total_score;
            map[driver.driver_id].reason_codes = driver.reason_codes;
          }
        });
      }
    });
    
    // Add assigned driver info if available
    if (trip.driverId) {
      const driverId = trip.driverId._id || trip.driverId;
      if (map[driverId]) {
        map[driverId].name = trip.driverId.name || map[driverId].name;
        map[driverId].phone = trip.driverId.phone || map[driverId].phone;
      }
    }
    
    console.log('Final driver map:', map);
    return map;
  }, [trip?.rideMatchLog, trip?.driverId, driverNames]);

    // Get the source page from URL search params
  const urlParams = new URLSearchParams(window.location.search);
  const sourcePage = urlParams.get('from');
  const sourceId = urlParams.get('sourceId');

  // Available status options
  const statusOptions = [
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'DIVERGED', label: 'Diverged' },
    { value: 'CANCELLED', label: 'Cancelled (All)' },
    { value: 'PICKEDUP', label: 'Picked up' },
    { value: 'ACCEPTED', label: 'Accepted' },
    { value: 'DROPPED', label: 'Dropped' },
    { value: 'failed', label: 'Failed' }
  ];

const statusChangeReasonOptions = [
  'Driver did not arrive',
  'Passenger cancelled trip',
  'Driver cancelled trip',
  'Driver delay',
  'Payment failure',
  'Booking timed out',
  'Network or API error',
  'Manual admin update',
  'Other'
]

  const getFinalStatusChangeReason = () => {
    if (!statusChangeReason) return '';
    if (statusChangeReason === 'Other') return String(statusChangeReasonOther || '').trim();
    return statusChangeReason;
  };

  // Function to update trip status
  const updateTripStatus = async () => {
    try {
      setIsUpdatingStatus(true);

      const finalReason = getFinalStatusChangeReason();
      if (!finalReason) {
        alert('Please select a reason (or enter Other reason).');
        setIsUpdatingStatus(false);
        return;
      }
      
      console.log('🔄 Updating trip status:', {
        tripId: id,
        oldStatus: trip.status,
        newStatus: selectedStatus
      });
      
      const response = await api.put(`/trips/${id}/status`, {
        status: selectedStatus,
        reason: finalReason
      });
      
      console.log('📨 Update response:', response.data);
      
      if (response.data.success) {
        console.log('✅ Status updated successfully');
        // Refetch trip data to get updated status
        window.location.reload();
      } else {
        console.error('❌ Update failed:', response.data);
        alert(`Failed to update status: ${response.data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error updating trip status:', error);
      
      // Show detailed error message
      let errorMessage = 'Error updating status. Please try again.';
      if (error.response) {
        console.error('Response error:', error.response.data);
        errorMessage = error.response.data.message || error.response.data.error || errorMessage;
      } else if (error.request) {
        console.error('Request error:', error.request);
        errorMessage = 'Network error. Please check your connection.';
      }
      
      alert(errorMessage);
    } finally {
      setIsUpdatingStatus(false);
      setShowConfirmModal(false);
      setShowEditStatusModal(false);
      setSelectedStatus('');
      setStatusChangeReason('');
      setStatusChangeReasonOther('');
    }
  };

  // Handle edit status button click
  const handleEditStatus = () => {
    setSelectedStatus(trip.status);
    setStatusChangeReason('');
    setStatusChangeReasonOther('');
    setShowEditStatusModal(true);
  };

  // Handle status selection and show confirmation
  const handleStatusSelect = () => {
    if (selectedStatus && selectedStatus !== trip.status) {
      setShowEditStatusModal(false);
      setShowConfirmModal(true);
    } else {
      setShowEditStatusModal(false);
    }
  };

  // Determine the back navigation based on source
  const getBackLinkProps = () => {
    if (location.state?.from) {
      const { from, ...restState } = location.state;
      return { to: from, state: restState };
    }
    return null;
  };

  const getBackUrl = () => {
    if (location.state?.from) {
      return location.state.from;
    }
    if (sourcePage && sourceId) {
      switch (sourcePage) {
        case 'driver':
          return `/drivers/${sourceId}`;
        case 'passenger':
          return `/passengers/${sourceId}`;
        case 'vendor':
          return `/vendors/${sourceId}`;
        case 'regional-office':
          return `/regional-offices/${sourceId}`;
        case 'vehicle':
          return `/vehicles/${sourceId}`;
        case 'payment':
          const sourceType = urlParams.get('sourceType');
          if (sourceType === 'driver') {
            return `/payments/due/driver/${sourceId}/trips`;
          } else if (sourceType === 'vendor') {
            return `/payments/due/vendor/${sourceId}/trips`;
          }
          return '/payments/due';
        case 'financial':
          const financialSourceType = urlParams.get('sourceType');
          if (financialSourceType === 'driver') {
            return `/financial/due/driver/${sourceId}/trips`;
          } else if (financialSourceType === 'vendor') {
            return `/financial/due/vendor/${sourceId}/trips`;
          } else if (financialSourceType === 'gst') {
            return `/financial/gst`;
          } else if (financialSourceType === 'office') {
            return `/financial/due/office/${sourceId}`;
          }
          return '/financial/due';
        case 'gst-details':
          return '/financial/gst';
        case 'due-details':
          return `/financial/due/office/${sourceId}`;
        default:
          return '/trips';
      }
    }
    return '/trips';
  };


  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="empty-state">
        <AlertCircle className="empty-state-icon" />
        <h3 className="empty-state-title">Trip not found</h3>
        <p className="empty-state-description">
          The trip you're looking for doesn't exist.
        </p>
        <Link to={getBackUrl()} className="btn btn-primary mt-4">
          Go Back
        </Link>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="empty-state">
        <AlertCircle className="empty-state-icon" />
        <h3 className="empty-state-title">Trip not found</h3>
        <p className="empty-state-description">
          The trip you're looking for doesn't exist.
        </p>
        <Link to={getBackUrl()} className="btn btn-primary mt-4">
          Go Back
        </Link>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      'PENDING': { class: 'badge-warning', text: 'Waiting for Driver', icon: Clock },
      'ASSIGNED': { class: 'badge-info', text: 'Assigned', icon: CheckCircle },
      'STARTED': { class: 'badge-primary', text: 'Started', icon: Play },
      'IN_PROGRESS': { class: 'badge-primary', text: 'In Progress', icon: Navigation },
      'COMPLETED': { class: 'badge-success', text: 'Completed', icon: CheckCircle },
      'CANCELLED': { class: 'badge-danger', text: 'Cancelled', icon: XCircle },
      'DROPPED': { class: 'badge-danger', text: 'Dropped', icon: Ban },
      'DIVERGED': { class: 'badge-warning', text: 'Diverged', icon: Navigation },
      'CANCELLED_BEFORE_PICKUP': { class: 'badge-danger', text: 'Cancelled - Before Pickup', icon: XCircle },
      'CANCELLED_AFTER_PICKUP': { class: 'badge-danger', text: 'Cancelled - After Pickup', icon: XCircle },
      'PICKEDUP': { class: 'badge-primary', text: 'Picked Up', icon: CheckCircle },
      'ACCEPTED': { class: 'badge-info', text: 'Accepted', icon: CheckCircle },
      'failed': { class: 'badge-danger', text: 'Driver NOT Accepted', icon: AlertCircle }
    };

    const config = statusConfig[status] || { class: 'badge-info', text: status, icon: Clock };
    const IconComponent = config.icon;
    return (
      <span className={`badge ${config.class} flex items-center space-x-1`}>
        <IconComponent className="h-3 w-3" />
        <span>{config.text}</span>
      </span>
    );
  };

  const getPaymentMethodBadge = (method) => {
    const methodConfig = {
      'CASH': { class: 'badge-success', text: 'Cash' },
      'UPI': { class: 'badge-info', text: 'UPI' },
    };

    const config = methodConfig[method] || { class: 'badge-info', text: method };
    return <span className={`badge ${config.class}`}>{config.text}</span>;
  };

  const formatLocation = (location) => {
    if (!location || !Array.isArray(location) || location.length < 2) {
      return 'N/A';
    }
    return `${location[1].toFixed(4)}, ${location[0].toFixed(4)}`;
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours} hrs ${mins} mins` : `${mins} mins`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Timestamps reach us in two different units: acting-driver trips store
  // milliseconds, normal rides store (fractional) seconds. Normalise before
  // formatting — a fixed `* 1000` renders acting timestamps as the year 58656.
  const formatEpoch = (value) => {
    if (!value) return 'N/A';
    const num = Number(value);
    if (Number.isNaN(num)) return formatDate(value);
    return formatDate(num < 1e12 ? num * 1000 : num);
  };

  const formatCancellationTime = (time) => formatEpoch(time);

  // When the trip entered one of `states`, from its own timeline. Every trip records
  // {state, timestamp} as it moves (normal rides and acting trips alike), whereas the
  // dedicated fields differ: acting stores acceptedAt / pickedUpAt / droppedAt, a
  // normal ride stores none of assignedAt / startTime / endTime for a driver-app
  // accept. Reading the timeline fills those rows for both, and for older trips.
  const timelineAt = (states) => {
    const hit = [...(Array.isArray(trip?.tripTimeline) ? trip.tripTimeline : [])]
      .reverse()
      .find((entry) => states.includes(entry?.state) && Number(entry?.timestamp) > 0);
    return hit ? Number(hit.timestamp) : null;
  };
  const assignedAtValue = trip?.assignedAt || trip?.acceptedAt || timelineAt(['ACCEPTED']);
  const startedAtValue = trip?.startTime || trip?.pickedUpAt || timelineAt(['PICKEDUP']);
  const endedAtValue = trip?.endTime || trip?.droppedAt
    || timelineAt(['DROPPED', 'COMPLETED', 'PAYMENT_COMPLETED', 'DIVERGED']);
  const pickedUpAtValue = (trip?.stops?.[0]?.stopUpdated && trip.stops[0].updatedAt)
    || trip?.pickedUpAt || timelineAt(['PICKEDUP']);

  // An acting driver is booked FOR a window — often tomorrow — so every actual
  // time (start/end/picked up) is legitimately N/A until it happens. Without the
  // booked window shown, the panel looks empty for a perfectly healthy booking.
  const isActingTrip = trip.isActingDriverTrip || trip.serviceType === 'ACTING';
  const actingTerms = trip.actingDetails?.overtimeTerms || {};
  const actingStartMs = trip.scheduleDateTime ? Number(trip.scheduleDateTime) : null;
  const actingBookedHours = Number(actingTerms.bookedHours) || 0;
  const actingBookedDays = Number(actingTerms.bookedDays) || 0;
  // The engine derives the same window this way (scheduleDateTime + bookedHours),
  // so the dashboard agrees with what drivers were actually offered.
  const actingEndMs = actingStartMs && actingBookedHours
    ? actingStartMs + actingBookedHours * 3600000
    : null;
  const actingDurationLabel = [
    actingBookedDays ? `${actingBookedDays} day${actingBookedDays === 1 ? '' : 's'}` : null,
    actingBookedHours ? `${actingBookedHours} hour${actingBookedHours === 1 ? '' : 's'}` : null,
  ].filter(Boolean).join(' ') || 'N/A';

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to={getBackLinkProps()?.to ?? getBackUrl()}
                state={getBackLinkProps()?.state}
                className="text-gray-400 hover:text-gray-600"
              >
                <ArrowLeft className="h-6 w-6" />
              </Link>
              <div>
                <h1 className="page-title">Trip Details</h1>
                <p className="page-subtitle">Trip ID: {trip._id || trip.id || trip.tripId}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {getStatusBadge(displayTripStatus(trip))}
              <button
                onClick={handleEditStatus}
                className="btn btn-secondary flex items-center space-x-1"
                title="Edit Status"
              >
                <Edit className="h-4 w-4" />
                <span>Edit Status</span>
              </button>
              <button
                onClick={() => setShowMap(!showMap)}
                className="btn btn-secondary"
              >
                {showMap ? <X className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                {showMap ? 'Hide Map' : 'Show Map'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Trip Route Map */}
      {showMap && (
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Trip Route Map</h3>
              {!trip.encodedPolyline && (
                ['DROPPED', 'COMPLETED', 'PAYMENT_COMPLETED', 'CANCELLED', 'DIVERGED'].includes(trip.status)
                  ? (
                    // The trip is over and nothing was saved: that IS a gap worth flagging.
                    <span className="text-sm text-orange-600 bg-orange-50 px-2 py-1 rounded-md">
                      Route path not found
                    </span>
                  )
                  : (
                    // The route driven is saved when the trip ends. Until then the map shows
                    // the route so far, from the driver's GPS, if there is any. Not a fault.
                    <span className="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded-md">
                      {liveRoutePolyline
                        ? `Live route so far${liveRouteData?.distanceKm ? ` · ${liveRouteData.distanceKm} km` : ''} · updates every 10s`
                        : 'Route is saved when the trip ends'}
                    </span>
                  )
              )}
            </div>
          </div>
          <div className="card-body p-0">
            <TripMap
              tripDetails={mapTrip}
              height="600px" 
              driverLocation={driverLocation}
              onRefreshDriver={shouldFetchDriverLocation ? handleRefreshDriverLocation : null}
            />
          </div>
        </div>
      )}

      {/* Comprehensive Trip Information Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-6">Trip Information</h2>

        {/* Trip Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4 mb-6">
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center">
              <Calendar className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-green-900">Ride ID</p>
                <p className="text-xs text-green-700">{trip.rideId || 'N/A'}</p>
              </div>
            </div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center">
              <MapPin className="h-8 w-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-purple-900">Vehicle Type</p>
                <p className="text-xs text-purple-700">{trip.vehicleType || 'N/A'}</p>
              </div>
            </div>
          </div>
          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center">
              <User className="h-8 w-8 text-orange-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-orange-900">Passengers</p>
                <p className="text-xs text-orange-700">{trip.passangerCount || 1}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Trip Route Details */}
          <div className="lg:col-span-1">
            <h3 className="font-medium text-gray-700 mb-4">Route & Stops</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-3 h-3 bg-green-500 rounded-full mt-2"></div>
                <div>
                  <p className="font-medium text-gray-900">Pickup Location</p>
                  <p className="text-gray-600 text-sm break-words">{formatLocation(trip.startLocation)}</p>
                  {trip.stops && trip.stops[0] && (
                    <div className="mt-1 space-y-2">
                      {trip.stops[0].arrivalTime && trip.stops[0].address && (
                     <p className='text-green-600 text-xs  mt-1'>Arrival Time: {new Date(trip.stops[0].arrivalTime).toLocaleTimeString()}</p>)}
                    <p className="text-gray-500 text-xs">{trip.stops[0].address}</p>
                    </div>
                  )}
                </div>
              </div>

              {trip.stops && trip.stops.length > 2 && (
                trip.stops.slice(1, -1).map((stop, index) => (
                  <div key={index}>
                    <div className="border-l-2 border-gray-300 ml-1.5 h-4"></div>
                    <div className="flex items-start space-x-3">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full mt-2"></div>
                      <div>
                        <p className="font-medium text-gray-900">{stop.name}</p>
                        <p className="text-gray-600 text-sm break-words mt-1">{stop.address || formatLocation(stop.location)}</p>
                        {stop.waitingTime > 0 && (
                          <p className="text-orange-600 text-xs mt-1"> Passenger Requested Wait: {stop.waitingTime} mins</p>
                        )}
                        {stop.isReached && (
                          <p className="text-green-600 text-xs">✓ Reached at {new Date(stop.arrivalTime).toLocaleTimeString()}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}

              <div className="border-l-2 border-gray-300 ml-1.5 h-4"></div>
              <div className="flex items-start space-x-3">
                <div className="w-3 h-3 bg-red-500 rounded-full mt-2"></div>
                <div>
                  <p className="font-medium text-gray-900">Drop Location</p>
                  <p className="text-gray-600 text-sm break-words">{formatLocation(trip.endLocation)}</p>
                  {trip.stops && trip.stops[trip.stops.length - 1] && (
                    <div className="mt-1 space-y-1">
                       {trip.stops[0].arrivalTime && trip.stops[0].address && trip.status !== 'PICKEDUP' && trip.status !== 'ACCEPTED' && trip.status !== 'STARTED' &&(
                    <p className='text-orange-600 text-xs'>Drop Time: {trip.stops[trip.stops.length - 1].arrivalTime ? new Date(trip.stops[trip.stops.length - 1].arrivalTime).toLocaleTimeString() : 'N/A'}</p>)}
                    <p className="text-gray-500 text-xs">{trip.stops[trip.stops.length - 1].address}</p>
                    {(trip.status === "COMPLETED" || trip.status === "DIVERGED") && (
                    <p className='text-orange-600 text-xs'>Total Driver wait time: {trip.stops[trip.stops.length - 1].driverWaitTime} mins</p>
                    )}
                    </div>
                  )}
                </div>
              </div>

              {/* Cancelled Trip Details under Route & Stops */}
              {(trip.status === 'CANCELLED' ||
                trip.status === 'CANCELLED_BEFORE_PICKUP' ||
                trip.status === 'CANCELLED_AFTER_PICKUP' ||
                trip.cancelledBy) && (
                <div>
                  <div className="border-l-2 border-gray-300 ml-1.5 h-4"></div>
                  <div className="flex items-start space-x-3">
                    <div className="w-3 h-3 bg-red-600 rounded-full mt-2"></div>
                    <div>
                      <p className="font-medium text-gray-900">
                        Cancelled By: <span className="text-gray-900">{trip.cancelledBy ? String(trip.cancelledBy).toUpperCase() : 'N/A'}</span>
                      </p>
                      <p className="text-red-600 text-xs mt-1">
                        Cancellation Reason: {cancelReasons(trip.cancellationReason || trip.cancelReason) || trip.cancellationReason || trip.cancelReason || 'N/A'}
                      </p>
                      <p className="text-gray-500 text-xs mt-1">
                        Cancellation Time: {formatCancellationTime(trip.cancellationTime || trip.cancelledAt)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Trip Details */}
          <div className="lg:col-span-1">
           <div className="flex justify-between items-center mb-4">
            <h3 className="font-medium text-gray-700">Trip Details</h3> 
            {/* <button
              onClick={() => setShowTripJsonModal(true)}
              className='font-medium mb-4 bg-gray-100 px-2 rounded border'
            >
              {"{}"}
            </button> */}
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span>{getStatusBadge(displayTripStatus(trip))}</span>
              </div>
              {(trip.status === 'CANCELLED' || trip.status === 'CANCELLED_BEFORE_PICKUP' || trip.status === 'CANCELLED_AFTER_PICKUP' || trip.cancelledBy) && (
                <div className='space-y-3'>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cancelled By:</span>
                    <span className='text-md font-medium'>{trip.cancelledBy ? String(trip.cancelledBy).toUpperCase() : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cancellation Reason:</span>
                    <span className='text-md font-medium'>{cancelReasons(trip.cancellationReason || trip.cancelReason) || trip.cancellationReason || trip.cancelReason || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cancellation Time:</span>
                    <span className='text-md font-medium'>{formatCancellationTime(trip.cancellationTime || trip.cancelledAt)}</span>
                  </div>
                </div>
              )}
               {trip.status !== 'CANCELLED' && trip.status !== 'CANCELLED_BEFORE_PICKUP' && trip.status !== 'CANCELLED_AFTER_PICKUP' && (
              <div className="flex justify-between">
                <span className="text-gray-600">OTP:</span>
                <span className="font-medium text-blue-600">{trip.otp || 'N/A'}</span>
              </div>
               )}
              <div className="flex justify-between">
                <span className="text-gray-600">Booking Time:</span>
                <span className="font-medium">{trip.bookingTime ? formatDate(trip.bookingTime) : 'N/A'}</span>
              </div>

              {/* The booked window — what the customer actually reserved. */}
              {isActingTrip && actingStartMs && (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Scheduled For:</span>
                    <span className="font-medium text-blue-600">{formatEpoch(actingStartMs)}</span>
                  </div>
                  {actingEndMs && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Scheduled Until:</span>
                      <span className="font-medium">{formatEpoch(actingEndMs)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Booked Duration:</span>
                    <span className="font-medium">{actingDurationLabel}</span>
                  </div>
                </>
              )}

              <div className="flex justify-between">
                <span className="text-gray-600">Assigned At:</span>
                <span className="font-medium">{formatEpoch(assignedAtValue)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Start Time:</span>
                <span className="font-medium">{formatEpoch(startedAtValue)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">End Time:</span>
                <span className="font-medium">{formatEpoch(endedAtValue)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Picked Up Time:</span>
                <span className="font-medium">{formatEpoch(pickedUpAtValue)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Booking For:</span>
                <span className="font-medium">{trip.bookingFor || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Night Ride:</span>
                <span className={`px-2 py-1 text-xs rounded-full ${trip.nightRide ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                  {trip.nightRide ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Female Only:</span>
                <span className={`px-2 py-1 text-xs rounded-full ${trip.femaleOnly ? 'bg-pink-100 text-pink-800' : 'bg-gray-100 text-gray-800'}`}>
                  {trip.femaleOnly ? 'Yes' : 'No'}
                </span>
              </div>

              {/* Client build metadata - which app version booked this trip */}
              <div className="pt-3 mt-1 border-t border-gray-200 space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Booking App Version:</span>
                  <span className="font-mono text-sm font-medium break-all text-right">
                    {trip.appVersion || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Booking Build Number:</span>
                  <span className="font-mono text-sm font-medium">{trip.buildNumber || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Fare & Distance Details */}
          <div className="lg:col-span-1">
            <div className="flex justify-between items-center mb-4">
            <h3 className="font-medium text-gray-700">Fare & Distance</h3>
            {/* <button
              onClick={() => setShowFareJsonModal(true)}
              className='font-medium mb-4 bg-gray-100 px-2 rounded border'
            >
              {"{}"}
            </button> */}
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Min Fare:</span>
                <span className="font-medium">₹{trip.minFare?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Max Fare:</span>
                <span className="font-medium">₹{trip.maxFare?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Estimated Fare:</span>
                <span className="font-medium">₹{trip.estimatedFare?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Payment Method:</span>
                <span>{getPaymentMethodBadge(trip.paymentMethod)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Final Fare:</span>
                <span className="font-medium underline">{trip.fareDetails && trip.fareDetails.fare != null ? `₹${trip.fareDetails.fare.toFixed(2)}` : 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Estimated Distance:</span>
                <span className="font-medium">{trip.estimatedDistance?.toFixed(2) || '0'} km</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Final Distance:</span>
                <span className="font-medium">{trip.finalDistance?.toFixed(2) || '0'} km</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Estimated Duration:</span>
                <span className="font-medium">{trip.estimatedDuration || 0} mins</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Final Duration:</span>
                <span className="font-medium">{trip.finalDuration ? formatDuration(trip.finalDuration) : '0 mins'}</span>
              </div>
              {trip.isPaymentIssue && (
                <div className="flex justify-between">
                  <span className="text-red-600 font-medium">Payment Issue:</span>
                  <span className="font-medium">{trip.passengerPaymentIssue || ''}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* User Details Section - Matching Ticket UI */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="font-medium text-gray-700 mb-4">User Details</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
            {/* Passenger Details */}
            <Link
              to={`/passengers/${trip.passangerId || trip.userId}?from=trip&sourceId=${trip._id || trip.id}`}
              className="bg-blue-50 p-4 rounded-lg hover:bg-blue-100 transition-colors cursor-pointer"
            >
              <h4 className="font-medium text-blue-900 mb-3">Passenger Information</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-blue-700">Name:</span>
                  <span className="font-medium truncate max-w-32">{trip.bookingForName || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-700">Phone:</span>
                  <span className="font-medium truncate max-w-32">{trip.bookingForPhone || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-700">Gender:</span>
                  <span className="font-medium truncate max-w-32">{trip.bookingForGender || 'N/A'}</span>
                </div>
              </div>
            </Link>

            {/* Driver Details */}
            <Link
              to={`/drivers/${trip.driverId?._id}?from=trip&sourceId=${trip._id || trip.id}`}
              className="bg-green-50 p-4 rounded-lg hover:bg-green-100 transition-colors cursor-pointer"
            >
              <h4 className="font-medium text-green-900 mb-3">Driver Information</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-green-700">Name:</span>
                  <span className="font-medium truncate max-w-32">{trip.driverId?.name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-700">Phone:</span>
                  <span className="font-medium truncate max-w-32">{trip.driverId?.phone || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-700">License:</span>
                  <span className="font-medium truncate max-w-32">{trip.driverId?.licenseNo || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-700">Vehicle Modal:</span>
                  <span className="font-medium truncate max-w-32">
                    {trip.driverId?.vehicleDetails?.model || trip.vehicleId?.model || 'N/A'}
                  </span>
                </div>

                {/* Driver app + handset, from the driver's last reported deviceMeta */}
                <div className="pt-2 mt-1 border-t border-green-200 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-green-700">Driver App:</span>
                    <span className="font-mono text-xs font-medium truncate max-w-32">
                      {trip.driverId?.deviceMeta?.appVersion || 'N/A'}
                      {trip.driverId?.deviceMeta?.buildNumber ? ` (${trip.driverId.deviceMeta.buildNumber})` : ''}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-700">Driver OS:</span>
                    <span className="font-medium truncate max-w-32">
                      {trip.driverId?.deviceMeta?.os
                        ? `${trip.driverId.deviceMeta.os} ${trip.driverId.deviceMeta.osVersion || ''}`.trim()
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-700">Driver Phone:</span>
                    <span className="font-medium truncate max-w-32">
                      {trip.driverId?.deviceMeta?.brand
                        ? `${trip.driverId.deviceMeta.brand} ${trip.driverId.deviceMeta.model || ''}`.trim()
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </Link>

            {/* Regional Office Details */}
            <Link
              to={`/regional-offices/${trip.regionalOffice?.id}?from=trip&sourceId=${trip._id || trip.id}`}
              className="bg-purple-50 p-4 rounded-lg hover:bg-purple-100 transition-colors cursor-pointer"
            >
              <h4 className="font-medium text-purple-900 mb-3">Regional Office</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-purple-700">Office Name:</span>
                  <span className="font-medium truncate max-w-32">{trip.regionalOffice?.name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-purple-700">Address:</span>
                  <span className="font-medium truncate max-w-32">{trip.regionalOffice?.address || 'N/A'}</span>
                </div>
              </div>
            </Link>

            {trip.vendorId && (
              /* Vendor Details */
              <Link
                to={`/vendors/${trip.vendorId?._id}?from=trip&sourceId=${trip._id || trip.id}`}
                className="bg-orange-50 p-4 rounded-lg hover:bg-orange-100 transition-colors cursor-pointer"
              >
                <h4 className="font-medium text-orange-900 mb-3">Vendor Information</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-orange-700">Vendor Name:</span>
                    <span className="font-medium truncate max-w-32">{trip.vendorId?.VendorName || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-orange-700">Owner Name:</span>
                    <span className="font-medium truncate max-w-32">{trip.vendorId?.OwnerName || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-orange-700">Phone:</span>
                    <span className="font-medium truncate max-w-32">{trip.vendorId?.ownerPhone || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-orange-700">Email:</span>
                    <span className="font-medium truncate max-w-32">{trip.vendorId?.ownerEmail || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-orange-700">Fleet ID:</span>
                    <span className="font-medium truncate max-w-32">{trip.vendorId?.fleetSysId || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-orange-700">Status:</span>
                    <span className="font-medium">
                      {trip.vendorId?.isApproved ? 'Approved' : trip.vendorId?.isBlocked ? 'Blocked' : 'Pending'}
                    </span>
                  </div>
                </div>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Ratings Section */}
      {(trip.passengerRating || trip.driverRating) && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Ratings</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Passenger Rating */}
            {trip.passengerRating && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-3">Passenger Rating (by Driver)</h4>
                <div className="flex items-center space-x-2 mb-2">
                  <Star className="h-5 w-5 text-yellow-400" />
                  <span className="text-lg font-medium text-gray-900">
                    {trip.passengerRating.rating}/5
                  </span>
                </div>
                {trip.passengerRating.comment && (
                  <p className="text-sm text-gray-600">
                    "{trip.passengerRating.comment}"
                  </p>
                )}
              </div>
            )}

            {/* Driver Rating */}
            {trip.driverRating && (
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-medium text-green-900 mb-3">Driver Rating (by Passenger)</h4>
                <div className="flex items-center space-x-2 mb-2">
                  <Star className="h-5 w-5 text-yellow-400" />
                  <span className="text-lg font-medium text-gray-900">
                    {trip.driverRating.rating}/5
                  </span>
                </div>
                {trip.driverRating.comment && (
                  <p className="text-sm text-gray-600">
                    "{trip.driverRating.comment}"
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {tripTimeline && tripTimeline.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="font-medium text-gray-700 mb-4">Trip Timeline</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
              {/* Passenger Details */}
              <div className="p-4 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-800">Timeline</h4>
                </div>
                <ol className="relative border-l border-gray-200 ml-3">
                  {tripTimeline.map((t, i) => {
                    const firstTs = tripTimeline[0]?.timestampMs;
                    const cumulative = t.timestampMs && firstTs ? t.timestampMs - firstTs : null;
                    const display = timelineMode === 'cumulative' ? (cumulative != null ? `Since start ${formatDurationShort(cumulative)}` : 'N/A') : (t.diffMs != null ? `Δ ${formatDurationShort(t.diffMs)}` : 'Start');
                    const stateLabel = (t.state || t.status || '').replace(/_/g, ' ');
                    const colorClass = getTimelineBadgeClass(t.state);
                    return (
                      <li key={i} className="mb-6 ml-6">
                        <span className={`absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full ${colorClass} ring-8 ring-white`}></span>
                        <div className="text-sm font-semibold text-gray-900">{stateLabel}</div>
                        <div className="text-xs text-gray-500">{t.timestampMs ? new Date(t.timestampMs).toLocaleString() : 'N/A'}</div>
                        <div className="text-xs text-gray-600 mt-1">{display}</div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ride Match Log — acting trips. The acting engine logs in waves with its
          own event names (camelCase driverId), which the per-driver timeline
          below cannot read — it grouped everything under "undefined". This opens
          the same Ride Match history the Acting Trips list uses instead, and
          shows even before any log exists (a trip still searching). */}
      {(trip.isActingDriverTrip || trip.serviceType === 'ACTING') && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">Ride Match Log</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Which drivers were offered this trip, in which wave, and what each one did.
              </p>
            </div>
            <button
              onClick={() => setShowRideMatchModal(true)}
              className="btn btn-secondary flex items-center space-x-2"
            >
              <Users className="h-4 w-4" />
              <span>View Ride Match Log</span>
            </button>
          </div>
        </div>
      )}

      {/* Pre/post-trip vehicle photos and bills — acting trips only. */}
      {(trip.isActingDriverTrip || trip.serviceType === 'ACTING') && (
        <ActingTripMedia tripId={String(id)} />
      )}
      {showRideMatchModal && (
        <RideMatchModal
          tripId={String(id)}
          rideId={trip.rideId}
          onClose={() => setShowRideMatchModal(false)}
        />
      )}

      {/* Ride Match Log Section — normal rides */}
      {!(trip.isActingDriverTrip || trip.serviceType === 'ACTING') && trip.rideMatchLog && trip.rideMatchLog.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Ride Match Log</h2>
            <button
              onClick={() => setShowRideMatchMap(!showRideMatchMap)}
              className="btn btn-secondary flex items-center space-x-2"
            >
              {showRideMatchMap ? <X className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              <span>{showRideMatchMap ? 'Hide Map' : 'Show Map'}</span>
            </button>
          </div>

          {showRideMatchMap && (
            <div className="mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <RideMatchMap 
                  tripDetails={trip} 
                  driverLocations={rideMatchDriverLocations}
                  driverNames={driverNames}
                  height="500px" 
                />
              </div>
            </div>
          )}

          {(() => {
            // Group events by driver_id
            const driverGroups = {};
            
            trip.rideMatchLog.forEach((log) => {
              const driverId = log.driver_id;
              if (!driverGroups[driverId]) {
                driverGroups[driverId] = [];
              }
              driverGroups[driverId].push(log);
            });

            // Extract available and ranked drivers from rideMatchLog
            const availableDriversEvent = trip.rideMatchLog.find(log => log.event === 'available_drivers');
            const rankedDriversEvent = trip.rideMatchLog.find(log => log.event === 'ranked_drivers');
            
            const availableDrivers = availableDriversEvent?.drivers?.map(d => d.driver_id) || [];
            const rankedDrivers = rankedDriversEvent?.top_drivers?.map(d => d.driver_id) || [];

            // Build a globally ordered list of events and a map from event object -> previous event timestamp (ms)
            const allEvents = trip.rideMatchLog
              .map(evt => ({ evt, ts: normalizeTs(evt.timestamp?.$numberLong || evt.timestamp) }))
              .filter(x => x.ts != null)
              .sort((a, b) => a.ts - b.ts);

            const prevTsMap = new Map();
            let lastTs = null;
            allEvents.forEach(({ evt, ts }) => {
              prevTsMap.set(evt, lastTs);
              lastTs = ts;
            });

            // Sort drivers by first event timestamp (using normalized timestamps)
            const sortedDriverIds = Object.keys(driverGroups).sort((a, b) => {
              const aTime = normalizeTs(driverGroups[a][0]?.timestamp?.$numberLong || driverGroups[a][0]?.timestamp) || 0;
              const bTime = normalizeTs(driverGroups[b][0]?.timestamp?.$numberLong || driverGroups[b][0]?.timestamp) || 0;
              return aTime - bTime;
            });

            // Helper function to get human-readable description
            const getEventDescription = (evt) => {
              if (evt.event === 'trip_request_attempt') {
                return 'Trip request was sent to this driver';
              } else if (evt.event === 'socket_message_sent') {
                return 'Notification delivered to driver\'s device';
              } else if (evt.event === 'driver_trip_response') {
                if (evt.response === 'accept') {
                  return 'Driver accepted the trip request';
                } else if (evt.response === 'reject') {
                  return 'Driver declined the trip request';
                } else if (evt.response === 'timeout') {
                  return 'Driver did not respond within the time limit';
                }
              } else if (evt.event === 'driver_not_connected') {
                return 'Driver not connected to the Socket';
              }
              return '';
            };

            return (
              <div className="space-y-6">
                {/* Available Drivers Section */}
                <div className="border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => { setAvailableSectionOpen(o => { if (o) setExpandedAvailableDrivers(new Set()); return !o; }); }}
                    className="w-full bg-blue-50 hover:bg-blue-100 px-4 py-3 flex items-center justify-between transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">Available Drivers</span>
                      <span className="text-sm text-gray-600">({[...new Set(availableDrivers)].length})</span>
                    </div>
                    <svg
                      className={`w-5 h-5 text-gray-600 transition-transform ${availableSectionOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {availableSectionOpen && (
                    <div className="p-4 bg-white space-y-4">
                      {[...new Set(availableDrivers)].map((driverId) => {
                        const driver = driverMap[driverId];
                        const events = driverGroups[driverId] || [];
                        const isExpanded = expandedAvailableDrivers.has(driverId);
                        return (
                          <div key={driverId} className="border border-gray-200 rounded-lg">
                            <button
                              onClick={() => setExpandedAvailableDrivers(prev => {
                                const next = new Set(prev);
                                isExpanded ? next.delete(driverId) : next.add(driverId);
                                return next;
                              })}
                              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                            >
                              <div className="text-left">
                                <div className="font-medium text-gray-900">
                                  {driver?.name ? driver.name.charAt(0).toUpperCase() + driver.name.slice(1).toLowerCase() : 'Unknown Driver'}
                                </div>
                                <div className="text-sm text-gray-600">{driver?.phone || 'N/A'}</div>
                              </div>
                              <svg
                                className={`w-4 h-4 text-gray-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            {isExpanded && (
                              <div className="px-4 pb-4 border-t border-gray-200">
                                <div className="mt-3 space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Driver ID:</span>
                                    <span className="font-mono text-xs">{driverId}</span>
                                  </div>
                                  {driver?.totalTripsAccepted != null && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Trips Accepted:</span>
                                      <span className="font-medium text-green-600">{driver.totalTripsAccepted}</span>
                                    </div>
                                  )}
                                  {driver?.totalTripsRejected != null && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Trips Rejected:</span>
                                      <span className="font-medium text-red-600">{driver.totalTripsRejected}</span>
                                    </div>
                                  )}
                                  {driver?.lastTripTime && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Last Trip:</span>
                                      <span className="font-medium text-xs">
                                        {new Date(normalizeTs(driver.lastTripTime.$numberLong || driver.lastTripTime)).toLocaleString()}
                                      </span>
                                    </div>
                                  )}
                                  {driver?.isAvailable != null && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Available:</span>
                                      <span className="font-medium">{driver.isAvailable ? 'Yes' : 'No'}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Ranked Drivers Section */}
                <div className="border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => { setRankedSectionOpen(o => { if (o) setExpandedRankedDrivers(new Set()); return !o; }); }}
                    className="w-full bg-purple-50 hover:bg-purple-100 px-4 py-3 flex items-center justify-between transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">Ranked Drivers</span>
                      <span className="text-sm text-gray-600">({[...new Set(rankedDrivers)].length})</span>
                    </div>
                    <svg
                      className={`w-5 h-5 text-gray-600 transition-transform ${rankedSectionOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {rankedSectionOpen && (
                    <div className="p-4 bg-white space-y-4">
                      {[...new Set(rankedDrivers)].map((driverId) => {
                        const driver = driverMap[driverId];
                        const events = driverGroups[driverId] || [];
                        const rankedEvent = rankedDriversEvent?.top_drivers?.find(d => d.driver_id === driverId);
                        const reasonCodes = rankedEvent?.reason_codes || [];
                        const rank = rankedEvent?.rank;
                        const score = rankedEvent?.total_score;
                        const isExpanded = expandedRankedDrivers.has(driverId);
                        return (
                          <div key={driverId} className="border border-gray-200 rounded-lg">
                            <button
                              onClick={() => setExpandedRankedDrivers(prev => {
                                const next = new Set(prev);
                                isExpanded ? next.delete(driverId) : next.add(driverId);
                                return next;
                              })}
                              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                            >
                              <div className="text-left">
                                <div className="font-medium text-gray-900">
                                  {driver?.name ? driver.name.charAt(0).toUpperCase() + driver.name.slice(1).toLowerCase() : 'Unknown Driver'}
                                </div>
                                <div className="text-sm text-gray-600">{driver?.phone || 'N/A'}</div>
                              </div>
                              <svg
                                className={`w-4 h-4 text-gray-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            {isExpanded && (
                              <div className="px-4 pb-4 border-t border-gray-200">
                                <div className="mt-3 space-y-3">
                                  <div className="text-sm space-y-2">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Driver ID:</span>
                                      <span className="font-mono text-xs">{driverId}</span>
                                    </div>
                                    {rank && (
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Rank:</span>
                                        <span className="font-medium">{rank}</span>
                                      </div>
                                    )}
                                    {score && (
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Score:</span>
                                        <span className="font-medium">{score.toFixed(4)}</span>
                                      </div>
                                    )}
                                  </div>
                                  {reasonCodes.length > 0 && (
                                    <div>
                                      <div className="text-sm font-medium text-gray-700 mb-2">Reason Codes:</div>
                                      <div className="space-y-1">
                                        {reasonCodes.map((code, idx) => (
                                          <div key={idx} className="text-xs bg-gray-50 px-2 py-1 rounded border border-gray-200">
                                            {code.replace(/_/g, ' ')}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Original Timeline View - Only show drivers with action events */}
                {(() => {
                  // Filter drivers who have actual timeline events (not just available/ranked)
                  const driversWithTimeline = sortedDriverIds.filter(driverId => {
                    const events = driverGroups[driverId];
                    return events && events.some(evt => 
                      evt.event === 'trip_request_attempt' || 
                      evt.event === 'socket_message_sent' || 
                      evt.event === 'driver_trip_response' || 
                      evt.event === 'driver_not_connected' ||
                      evt.event === 'admin_assignment'
                    );
                  });

                  if (driversWithTimeline.length === 0) return null;

                  return (
                    <>
                      <div className="border-t-2 border-gray-300 pt-6 mt-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Detailed Timeline</h3>
                      </div>
                      {driversWithTimeline.map((driverId, idx) => {
                        const events = driverGroups[driverId];
                        const driver = driverMap[driverId];

                        return (
                    <div key={driverId} className="border border-gray-200 rounded-lg p-4 bg-gray-50 max-w-5xl">
                      {/* Driver Header */}
                      <div className="mb-4 pb-3 border-b border-gray-300">
                        <Link to={`/drivers/${trip.driverId?._id}?from=trip&sourceId=${trip._id}`}>
                        <h3 className="text-base font-semibold text-gray-900">
                          {driver?.name || 'Unknown Driver'}
                        </h3>
                        </Link>
                        <p className="text-sm text-gray-600 mt-1">{driver?.phone || 'N/A'}</p>
                        <p className="text-xs text-gray-500 mt-1">Driver ID: {driverId}</p>
                      </div>

                      {/* Timeline */}
                      <div className="relative pl-8">
                        {/* Vertical Line */}
                        <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-gray-300"></div>

                        {/* Events */}
                        {events.map((evt, evtIdx) => {
                          const timestamp = evt.timestamp?.$numberLong || evt.timestamp;
                          const tsMs = normalizeTs(timestamp);
                          const date = tsMs ? new Date(tsMs) : null;
                          const timeStr = date ? date.toLocaleTimeString('en-US', { 
                            hour: '2-digit', 
                            minute: '2-digit', 
                            second: '2-digit',
                            hour12: true 
                          }) : 'N/A';

                          // Determine dot color based on event type
                          let dotColor = 'bg-gray-400';
                          let eventBadge = { color: 'bg-gray-100 text-gray-800', label: 'Unknown' };
                          
                          if (evt.event === 'trip_request_attempt') {
                            dotColor = 'bg-blue-500';
                            eventBadge = { color: 'bg-blue-100 text-blue-800', label: 'Trip Request Sent' };
                          } else if (evt.event === 'socket_message_sent') {
                            dotColor = 'bg-purple-500';
                            eventBadge = { color: 'bg-purple-100 text-purple-800', label: 'Trip Message Sent' };
                          } else if (evt.event === 'driver_trip_response') {
                            if (evt.response === 'accept') {
                              dotColor = 'bg-green-500';
                              eventBadge = { color: 'bg-green-100 text-green-800', label: 'Accepted' };
                            } else if (evt.response === 'reject') {
                              dotColor = 'bg-red-500';
                              eventBadge = { color: 'bg-red-100 text-red-800', label: 'Rejected' };
                            } else {
                              dotColor = 'bg-yellow-500';
                              eventBadge = { color: 'bg-yellow-100 text-yellow-800', label: 'Timeout' };
                            }
                          } else if (evt.event === 'driver_not_connected') {
                            dotColor = 'bg-gray-500';
                            eventBadge = { color: 'bg-gray-100 text-gray-800', label: 'Not Connected' };
                          } else if (evt.event === 'admin_assignment') {
                            dotColor = 'bg-indigo-500';
                            eventBadge = { color: 'bg-indigo-100 text-indigo-800', label: 'Admin Assigned' };
                          }

                          const description = getEventDescription(evt);

                          return (
                            <div key={evtIdx} className={`relative ${evtIdx !== events.length - 1 ? 'pb-5' : ''}`}>
                              {/* Dot */}
                              <div className={`absolute -left-9 mt-1.5 h-6 w-6 rounded-full ${dotColor} ring-3 ring-white border-2 border-${dotColor.replace('bg-', 'border-')}`}></div>

                              {/* Event Content */}
                              <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
                                {/* Event Description and Time */}
                                <div className="flex items-start justify-between mb-3">
                                  <div className="items-start space-y-3">
                                    <div className="flex items-center gap-2">
                                      <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${eventBadge.color}`}>
                                        {eventBadge.label}
                                      </span>
                                      {(() => {
                                        const prevMsGlobal = prevTsMap.get(evt);
                                        const prevTimestampLocal = evtIdx > 0 ? (events[evtIdx - 1].timestamp?.$numberLong || events[evtIdx - 1].timestamp) : null;
                                        const prevMsLocal = normalizeTs(prevTimestampLocal);
                                        const prevMs = (typeof prevMsGlobal !== 'undefined' && prevMsGlobal !== null) ? prevMsGlobal : prevMsLocal;
                                        const deltaMs = (tsMs != null && prevMs != null) ? tsMs - prevMs : null;
                                        return deltaMs != null ? (
                                          <span className="text-xs text-gray-500">{`+${formatDurationShort(deltaMs)}`}</span>
                                        ) : null;
                                      })()}
                                    </div>
                                    <p className="text-sm font-semibold text-gray-900">{description}</p>
                                  </div>
                                  <div className="text-right ml-3 flex-shrink-0">
                                    <div className="text-sm font-medium text-gray-900">{timeStr}</div>
                                  </div>
                                </div>

                                {/* Event Details */}
                                <div className="space-y-2">
                                  {evt.fare && (
                                    <div className="flex items-center">
                                      <span className="text-sm text-gray-600 w-24">Fare:</span>
                                      <span className="text-sm font-semibold text-gray-900">{evt.currency || '₹'}{evt.fare}</span>
                                    </div>
                                  )}
                                  {evt.request_id && (
                                    <div className="flex items-center">
                                      <span className="text-sm text-gray-600 w-24">Request ID:</span>
                                      <span className="text-sm font-mono text-gray-700">{evt.request_id}</span>
                                    </div>
                                  )}
                                  {evt.total_score !== undefined && (
                                    <div className="flex items-center">
                                      <span className="text-sm text-gray-600 w-24">Match Score:</span>
                                      <span className="text-sm font-semibold text-gray-900">{evt.total_score}%</span>
                                    </div>
                                  )}
                                  {evt.reason_codes && evt.reason_codes.length > 0 && (
                                    <div className="mt-2">
                                      <span className="text-sm text-gray-600 block mb-1">Matching Details:</span>
                                      <div className="space-y-1">
                                        {evt.reason_codes.map((code, cIdx) => (
                                          <div key={cIdx} className="text-xs bg-gray-50 px-2 py-1 rounded border border-gray-200">
                                            {code}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {evt.reason && (
                                    <div className="flex items-center">
                                      <span className="text-sm text-gray-600 w-24">Reason:</span>
                                      <span className="text-sm text-gray-900">{evt.reason.replace(/_/g, ' ')}</span>
                                    </div>
                                  )}
                                  {evt.escalation_details && (
                                    <div className="mt-2 p-2 bg-orange-50 rounded text-sm border border-orange-200">
                                      <div className="font-medium text-orange-900">Escalation Count {evt.escalation_details.escalation_count}</div>
                                      <div className="text-orange-700 mt-1">
                                        Base Fare: ₹{evt.escalation_details.estimated_fare}
                                        {evt.escalation_details.escalation_bonus > 0 && (
                                          <span className="ml-2">• Bonus: +₹{evt.escalation_details.escalation_bonus}</span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                        </div>
                      );
                    })}
                  </>
                  );
                })()}
              </div>
            );
          })()}
        </div>
      )}
      
      {/* Edit Status Modal */}
      {showEditStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Trip Status</h3>
              <button
                onClick={() => {
                  setShowEditStatusModal(false);
                  setSelectedStatus('');
                  setStatusChangeReason('');
                  setStatusChangeReasonOther('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Status: {getStatusBadge(trip.status)}
              </label>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select New Status:
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select new status...</option>
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowEditStatusModal(false);
                  setSelectedStatus('');
                  setStatusChangeReason('');
                  setStatusChangeReasonOther('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusSelect}
                disabled={!selectedStatus || selectedStatus === trip.status}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Confirm Status Change</h3>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedStatus('');
                  setStatusChangeReason('');
                  setStatusChangeReasonOther('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to change the trip status?
              </p>
              <div className="bg-gray-50 p-3 rounded-md">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">From:</span>
                  {getStatusBadge(trip.status)}
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">To:</span>
                  {getStatusBadge(selectedStatus)}
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for change:
                </label>
                <select
                  value={statusChangeReason}
                  onChange={(e) => {
                    const next = e.target.value;
                    setStatusChangeReason(next);
                    if (next !== 'Other') setStatusChangeReasonOther('');
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select reason...</option>
                  {statusChangeReasonOptions.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>

                {statusChangeReason === 'Other' && (
                  <input
                    type="text"
                    value={statusChangeReasonOther}
                    onChange={(e) => setStatusChangeReasonOther(e.target.value)}
                    placeholder="Enter reason..."
                    className="mt-3 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                )}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedStatus('');
                  setStatusChangeReason('');
                  setStatusChangeReasonOther('');
                }}
                disabled={isUpdatingStatus}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={updateTripStatus}
                disabled={isUpdatingStatus || !getFinalStatusChangeReason()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {isUpdatingStatus && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                <span>{isUpdatingStatus ? 'Updating...' : 'Confirm Change'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trip JSON Modal - shows full trip JSON */}
      {showTripJsonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Trip Details (JSON)</h3>
              <button
                onClick={() => setShowTripJsonModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4 text-sm text-gray-700">
              <p>Full trip object (JSON).</p>
            </div>

            <div className="bg-gray-100 p-4 rounded overflow-auto" style={{ maxHeight: '60vh' }}>
              <pre className="text-xs">{JSON.stringify({ ...trip, paymentInfo: undefined }, null, 2)}</pre>
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowTripJsonModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fare JSON Modal - shows only fare-related JSON */}
      {showFareJsonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Fare Details (JSON)</h3>
              <button
                onClick={() => setShowFareJsonModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4 text-sm text-gray-700">
              <p>Full publicRidesPayments document data for this trip.</p>
            </div>

            <div className="bg-gray-100 p-4 rounded overflow-auto" style={{ maxHeight: '60vh' }}>
              <pre className="text-xs">
                {JSON.stringify(publicRidesPayments , null, 2)}
              </pre>
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowFareJsonModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TripDetails; 